<template>
  <div class="w-full">
    <table class="w-full text-sm">
      <thead>
        <tr class="bg-gray-50 border-b border-gray-200">
          <th
            colspan="4"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          />

          <th
            colspan="3"
            class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.costPerUnit') }}
          </th>

          <th
            colspan="3"
            class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.toDate') }}
          </th>

          <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
            {{ $t('CostAtComplete.WbsCodeList.table.toComplete') }}
          </th>

          <th
            colspan="2"
            class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.forecast') }}
          </th>
        </tr>

        <tr class="bg-gray-100 border-b border-gray-200">
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
            {{ $t('CostAtComplete.WbsCodeList.table.wbs') }}
          </th>

          <th
            class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            :title="$t('CostAtComplete.WbsCodeList.tooltip.tenderCost')"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.tenderCost') }}
          </th>

          <th
            class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            :title="$t('CostAtComplete.WbsCodeList.tooltip.finalQuantity')"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.finalQuantity') }}
          </th>

          <th
            class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            :title="$t('CostAtComplete.WbsCodeList.tooltip.unit')"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.unit') }}
          </th>

          <th
            class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            :title="$t('CostAtComplete.WbsCodeList.tooltip.tenderCostPerUnit')"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.tender') }}
          </th>

          <th
            class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            :title="$t('CostAtComplete.WbsCodeList.tooltip.toDateCostPerUnit')"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.toDate') }}
          </th>

          <th
            class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            :title="$t('CostAtComplete.WbsCodeList.tooltip.toCompleteCostPerUnit')"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.toComplete') }}
          </th>

          <th
            class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            :title="$t('CostAtComplete.WbsCodeList.tooltip.cost')"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.cost') }}
          </th>

          <th
            class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            :title="$t('CostAtComplete.WbsCodeList.tooltip.earnedValue')"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.earnedAmount') }}
          </th>

          <th
            class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            :title="$t('CostAtComplete.WbsCodeList.tooltip.variance')"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.variance') }}
          </th>

          <th
            class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            :title="$t('CostAtComplete.WbsCodeList.tooltip.costToComplete')"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.cost') }}
          </th>

          <th
            class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"
            :title="$t('CostAtComplete.WbsCodeList.tooltip.forecastCost')"
          >
            {{ $t('CostAtComplete.WbsCodeList.table.forecast') }}
          </th>

          <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
            {{ $t('CostAtComplete.WbsCodeList.table.profitLoss') }}
          </th>
        </tr>
      </thead>

      <tbody class="bg-white divide-y divide-gray-200">
        <template
          v-for="wbsCode in $props.wbsCodes"
          :key="wbsCode.id"
        >
          <WbsCodeListRow
            v-if="toCompleteTotals.get(wbsCode.id)"
            :wbs-code="wbsCode"
            :to-complete="toCompleteTotals.get(wbsCode.id)"
          />
        </template>

        <!-- Total Row -->
        <tr class="bg-gray-50 border-t-2 border-gray-300">
          <td class="px-6 py-4 text-sm font-semibold text-gray-900">
            {{ $t('CostAtComplete.WbsCodeList.table.total') }}
          </td>

          <td class="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
            {{ $tcc(totals.totalTenderCost) }}
          </td>

          <td
            colspan="5"
            class="px-6 py-4"
          />

          <td class="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
            {{ $tcc(totals.totalToDateCost) }}
          </td>

          <td class="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
            {{ $tcc(totals.totalEarnedAmount) }}
          </td>

          <td class="px-6 py-4 text-sm font-semibold text-right">
            <span
              :class="[totals.totalVariance >= 0 ? 'text-green-600' : 'text-red-600']"
            >
              {{ $tcc(totals.totalVariance) }}
            </span>
          </td>

          <td class="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
            {{ $tcc(totals.totalToCompleteCost) }}
          </td>

          <td class="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
            {{ $tcc(totals.totalForecastCost) }}
          </td>

          <td class="px-6 py-4 text-sm font-semibold text-right">
            <span
              :class="[totals.totalProfit >= 0 ? 'text-green-600' : 'text-red-600']"
            >
              {{ $tcc(totals.totalProfit) }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import WbsCode from '@/models/WbsCode';
import { computed } from 'vue';
import WbsCodeListRow from './WbsCodeListRow.vue';

const props = defineProps<{
  wbsCodes: WbsCode[];
  toCompleteTotals: Map<number, CostAtCompletion>;
}>();

const totals = computed(() => {
  let totalTenderCost = 0;
  let totalToDateCost = 0;
  let totalEarnedAmount = 0;
  let totalVariance = 0;
  let totalToCompleteCost = 0;
  let totalForecastCost = 0;
  let totalProfit = 0;

  // Calculate total tender cost from wbsCodes
  totalTenderCost = props.wbsCodes.reduce((total, wbsCode) => total + wbsCode.tender_cost, 0);

  // Calculate other totals from toCompleteTotals
  for(const [_key, value] of props.toCompleteTotals) {
    totalToDateCost += value.toDateCost;
    totalEarnedAmount += value.toDateEarnedAmount;
    totalVariance += value.toDateVariance;
    totalToCompleteCost += value.toCompleteCost;
    totalForecastCost += value.forecastCost;
    totalProfit += value.profit;
  }

  return {
    totalTenderCost,
    totalToDateCost,
    totalEarnedAmount,
    totalVariance,
    totalToCompleteCost,
    totalForecastCost,
    totalProfit,
  };
});
</script>

<style scoped></style>
